import {
  OrgTransactionResponseDTODataItem,
  TransactionResponseDTODataItem,
  WalletDTO,
} from "@/generated/api/models";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import { useEffect, useMemo, useState } from "react";
import { Pressable } from "@/components/ui/pressable";
import { EmptyState } from "@/screens/common/empty.state";
import { HStack } from "@/components/ui/hstack";
import { useTransactionListQuery } from "../transactions/useTransactionListQuery";
import { DatePickerBadge } from "../transactions/date.picker";
import { TransactionModal } from "../transactions/transaction.modal";
import { TransactionItemMini } from "../transactions/transaction.item";
import {
  useUserControllerMe,
  useUserWalletControllerGetWallet,
} from "@/generated/api/api";
import { getName, userHasName } from "@/utils/name";
import { Link as ExpoLink, useRouter } from "expo-router";
import { Heading } from "@/components/ui/heading";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import { SectionList } from "@/components/ui/section-list";
import { Box } from "@/components/ui/box";
import { useSumFormatter } from "@/utils/useSumFormatter";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { Info, Send } from "lucide-react-native";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { ActionList } from "../common/action.list";
import { WalletPayoutModal } from "./wallet.payout.modal";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { getActiveKYC } from "@/utils/kyc.util";

export function WalletCard({ wallet }: { wallet: WalletDTO }) {
  const { formatCents, currencySymbol } = useSumFormatter({
    asset: wallet.currency,
  });

  const [showProcessing, setShowProcessing] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [showBeneficiary, setShowBeneficiary] = useState(false);
  const [showKYC, setShowKYC] = useState(false);

  const { data: user } = useUserControllerMe();

  return (
    <>
      <Card
        size="md"
        variant="filled"
        className="flex-1 md:max-w-[50%] min-w-[40%]"
      >
        <Heading
          size="sm"
          className="mb-1 text-ellipsis overflow-hidden whitespace-nowrap"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {wallet.name ?? `${currencySymbol} Wallet`}
        </Heading>
        <VStack className="justify-center items-center gap-4">
          <Heading size="2xl">
            {formatCents(wallet.balance + wallet.holdBalance)}
          </Heading>
          <Button
            action="primary"
            isDisabled={wallet.balance + wallet.holdBalance <= 0}
            onPress={() => {
              if (
                !user?.beneficiary ||
                user.beneficiary.status === "DECLINED" ||
                user.beneficiary.status === "PENDING"
              ) {
                setShowBeneficiary(true);
              } else if (getActiveKYC(user?.kyc) !== undefined) {
                setShowKYC(true);
              } else {
                setShowPayout(true);
              }
            }}
          >
            <ButtonIcon as={Send} />
            <ButtonText>Request payout</ButtonText>
          </Button>
          {wallet.holdBalance > 0 && (
            <Pressable onPress={() => setShowProcessing(true)}>
              <Alert action="muted" variant="outline">
                <AlertIcon as={Info} />
                <AlertText>
                  {formatCents(
                    wallet.holdBalance + Math.min(wallet.balance, 0)
                  )}{" "}
                  is under processing
                </AlertText>
              </Alert>
            </Pressable>
          )}
        </VStack>
      </Card>
      <WalletPayoutModal
        wallet={wallet}
        isOpen={showPayout}
        onClose={() => setShowPayout(false)}
      />
      <UnderProcessingModal
        isOpen={showProcessing}
        onClose={() => setShowProcessing(false)}
      />
      <BeneficiaryRequiredModal
        isOpen={showBeneficiary}
        onClose={() => setShowBeneficiary(false)}
      />
      <KYCRequiredModal isOpen={showKYC} onClose={() => setShowKYC(false)} />
    </>
  );
}

function UnderProcessingModal(
  props: Pick<InterfaceModalProps, "onClose" | "isOpen">
) {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Under processing
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              Some of your funds are currently being processed by payment
              gateway and may not be fully available for payout.
            </Text>
            <Text>
              You may still request a payout, but it will be subject to
              availability at the time of your request.
            </Text>
            <Text>
              If the full amount is not accessible, feel free to try again
              later.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onPress={props.onClose}>
            <ButtonText>Understood</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function BeneficiaryRequiredModal(
  props: Pick<InterfaceModalProps, "onClose" | "isOpen">
) {
  const router = useRouter();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Bank details missing
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              Please, update your banking details to proceed with payouts.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            onPress={() => {
              router.replace("/(tabs)/profile");
              props.onClose?.();
            }}
          >
            <ButtonText>Go to profile</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function KYCRequiredModal(
  props: Pick<InterfaceModalProps, "onClose" | "isOpen">
) {
  const router = useRouter();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Identity verification required
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              Please, verify your identity to proceed with payouts. Please,
              check "Actions required" block.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            onPress={() => {
              props.onClose?.();
            }}
          >
            <ButtonText>Close</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function WalletPage() {
  const { data: user, refetch: refetchMe } = useUserControllerMe();

  const { data: systemWallets, refetch: refetchWallets } =
    useUserWalletControllerGetWallet();

  const wallets = useMemo(() => {
    if (!systemWallets) {
      // in case of internet errors or something else
      return systemWallets;
    }

    const defaultWallet: WalletDTO = {
      id: "placeholder_eur",
      balance: 0,
      holdBalance: 0,
      currency: "EUR/2",
    };

    if (!systemWallets.length) {
      return [defaultWallet];
    }

    if (
      systemWallets.findIndex((w) => w.currency === defaultWallet.currency) ===
      -1
    ) {
      return [...systemWallets, defaultWallet];
    }

    return systemWallets;
  }, [systemWallets]);

  const {
    infiniteQuery: {
      fetchNextPage,
      data: transactionsData,
      isFetchingNextPage,
      isLoadingError,
      hasNextPage,
      isRefetching,
      refetch,
      isSuccess,
    },
    setSelectedRange,
    selectedRange,
  } = useTransactionListQuery();

  const data = useMemo(
    () =>
      transactionsData?.pages.flatMap<
        OrgTransactionResponseDTODataItem | TransactionResponseDTODataItem
      >((p) => p.data),
    [transactionsData?.pages]
  );

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetTransaction, setSheetTransaction] = useState<
    OrgTransactionResponseDTODataItem | TransactionResponseDTODataItem
  >();

  return (
    <>
      <Box className="w-full h-full">
        <SectionList<
          | OrgTransactionResponseDTODataItem
          | TransactionResponseDTODataItem
          | "empty"
        >
          className="w-full h-full flex-1"
          contentContainerStyle={{
            flexGrow: 1,
            paddingVertical: 16,
            paddingHorizontal: 16,
          }}
          sections={[{ data: data && data.length > 0 ? data : ["empty"] }]}
          refreshing={isRefetching}
          onRefresh={() => {
            refetch();
            refetchMe();
            refetchWallets();
          }}
          keyExtractor={(info) => (typeof info === "string" ? info : info.id)}
          ItemSeparatorComponent={() => <Box className="min-h-4" />}
          renderSectionHeader={() =>
            data?.length !== 0 || !!selectedRange ? (
              <HStack className="min-h-6 w-full bg-background-0 justify-between py-4">
                <DatePickerBadge
                  selectedRange={selectedRange}
                  onRangeChange={(r) => setSelectedRange(r)}
                />
              </HStack>
            ) : null
          }
          extraData={{ isSuccess, isLoadingError }}
          renderItem={({ item }) => {
            if (typeof item === "string") {
              return (
                <VStack className="h-full w-full p-3">
                  {data?.length === 0 ? (
                    <EmptyState title="No transactions" />
                  ) : isLoadingError ? (
                    <EmptyState
                      title="Something went wrong"
                      description="Please check your internet connection and try again"
                    />
                  ) : (
                    <Spinner size="large" />
                  )}
                </VStack>
              );
            }

            return (
              <Pressable
                className="w-full"
                onPress={() => {
                  setSheetTransaction(item);
                  setSheetIsOpen(true);
                }}
              >
                <TransactionItemMini transaction={item} />
              </Pressable>
            );
          }}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          onEndReached={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={0.5}
          stickySectionHeadersEnabled
          ListHeaderComponent={
            <VStack className="bg-background-0 pb-0 w-full" space="2xl">
              <ActionList />
              {!userHasName(user) ? (
                <Heading size="2xl">Welcome!</Heading>
              ) : (
                <HStack className="items-center gap-2 flex-wrap">
                  <Heading size="2xl">Welcome,</Heading>
                  <ExpoLink href="/(tabs)/profile" replace>
                    <HStack className="gap-1 items-center">
                      <Avatar size="sm">
                        <AvatarFallbackText>{getName(user)}</AvatarFallbackText>
                        <AvatarImage
                          source={{ uri: user?.avatarImage ?? undefined }}
                        />
                      </Avatar>
                      <Heading size="2xl">{user?.firstName ?? ""}!</Heading>
                    </HStack>
                  </ExpoLink>
                </HStack>
              )}
              {!!wallets?.length && (
                <HStack className="flex-wrap gap-4">
                  {wallets.map((w) => (
                    <WalletCard key={w.id} wallet={w} />
                  ))}
                </HStack>
              )}
              <Heading size="xl">Transactions</Heading>
            </VStack>
          }
          ListFooterComponent={
            isFetchingNextPage ? <Spinner size="large" /> : undefined
          }
        />
      </Box>
      {sheetTransaction && (
        <TransactionModal
          transaction={sheetTransaction}
          isOpen={sheetIsOpen}
          onClose={() => setSheetIsOpen(false)}
        />
      )}
    </>
  );
}
